import { useMemo } from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ClickableTableCell, DashboardTable } from "./dashboard-table";
import { SeverityChip } from "~/components/severity-chip";
import {
  FindingsOrderField,
  OrderDirection,
  ScoreStateFilter,
  useGetFindingsQuery,
} from "~/operations";
import { formatDistanceToNowStrict } from "date-fns";
import DashboardSkeleton from "~/pages/organization/dashboard/DashboardSkeleton";
import type { SpaceEdge } from "./types";

export type LatestFixesProps = {
  spaceEdges: SpaceEdge[];
  spacesLoading: boolean;
};

type FixedFinding = {
  id: string;
  mrn: string;
  title: string | null | undefined;
  riskValue: number | null | undefined;
  lastUpdated: string;
  spaceName: string;
  spaceId: string;
  findingType: "cve" | "advisory" | "other";
};

export function LatestFixes({ spaceEdges, spacesLoading }: LatestFixesProps) {
  const spaceMrns = spaceEdges.slice(0, 3).map((e) => ({
    mrn: e.node.mrn,
    name: e.node.name,
  }));

  const findingsVars = {
    first: 10,
    orderBy: {
      field: FindingsOrderField.LastUpdated,
      direction: OrderDirection.Desc,
    },
    filter: {
      state: ScoreStateFilter.Closed,
    },
  };

  const space0 = useGetFindingsQuery({
    variables: { scopeMrn: spaceMrns[0]?.mrn || "", ...findingsVars },
    skip: !spaceMrns[0],
  });
  const space1 = useGetFindingsQuery({
    variables: { scopeMrn: spaceMrns[1]?.mrn || "", ...findingsVars },
    skip: !spaceMrns[1],
  });
  const space2 = useGetFindingsQuery({
    variables: { scopeMrn: spaceMrns[2]?.mrn || "", ...findingsVars },
    skip: !spaceMrns[2],
  });

  const isLoading =
    spacesLoading || space0.loading || space1.loading || space2.loading;

  const fixes = useMemo(() => {
    if (isLoading) return [];

    const results: FixedFinding[] = [];

    [
      { data: space0.data, mrn: spaceMrns[0]?.mrn, name: spaceMrns[0]?.name },
      { data: space1.data, mrn: spaceMrns[1]?.mrn, name: spaceMrns[1]?.name },
      { data: space2.data, mrn: spaceMrns[2]?.mrn, name: spaceMrns[2]?.name },
    ].forEach(({ data, mrn, name }) => {
      if (!data || !name || !mrn) return;
      const spaceId = mrn.split("/").pop() || "";
      const connection =
        data.findings?.__typename === "FindingsConnection"
          ? data.findings
          : null;
      connection?.edges?.forEach(({ node }) => {
        if (!node || !("id" in node)) return;
        const findingType =
          node.__typename === "CveFinding"
            ? "cve"
            : node.__typename === "AdvisoryFinding"
              ? "advisory"
              : "other";
        const mrn = "mrn" in node ? (node.mrn as string) : "";
        results.push({
          id: node.id,
          mrn,
          title: "title" in node ? node.title : null,
          riskValue: "riskValue" in node ? node.riskValue : undefined,
          lastUpdated: "lastUpdated" in node ? node.lastUpdated : "",
          spaceName: name,
          spaceId,
          findingType,
        });
      });
    });

    // Deduplicate by finding ID (e.g. CVE ID), keeping the most recently updated entry
    const byId = new Map<string, FixedFinding>();
    for (const r of results) {
      const existing = byId.get(r.id);
      if (
        !existing ||
        new Date(r.lastUpdated).getTime() >
          new Date(existing.lastUpdated).getTime()
      ) {
        byId.set(r.id, r);
      }
    }

    return [...byId.values()]
      .sort(
        (a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime(),
      )
      .slice(0, 10);
  }, [isLoading, space0.data, space1.data, space2.data, spaceMrns]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (fixes.length === 0) {
    return (
      <Typography
        sx={{
          fontWeight: "bold",
          color: "text.secondary",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        No recently fixed findings found
      </Typography>
    );
  }

  return (
    <DashboardTable>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: "15%", minWidth: "90px" }}>Fixed</TableCell>
          <TableCell sx={{ width: "10%", minWidth: "90px", pr: 0 }}>
            Risk Score
          </TableCell>
          <TableCell sx={{ width: "50%", pr: 0 }}>Finding</TableCell>
          <TableCell sx={{ width: "25%", minWidth: "80px" }}>Space</TableCell>
        </TableRow>
      </TableHead>
      <TableBody
        sx={[{ ".MuiTableCell-body": { fontSize: 12, cursor: "default" } }]}
      >
        {fixes.map((fix) => {
          const href =
            fix.findingType === "cve"
              ? `/space/vulns/cve/${fix.id}?spaceId=${fix.spaceId}`
              : fix.findingType === "advisory"
                ? `/space/vulns/advisory/${fix.id}?spaceId=${fix.spaceId}`
                : `/space/findings/all?spaceId=${fix.spaceId}`;
          return (
            <TableRow key={`${fix.spaceId}-${fix.id}`}>
              <ClickableTableCell
                sx={{ width: "15%", minWidth: "90px" }}
                href={href}
              >
                {fix.lastUpdated
                  ? formatDistanceToNowStrict(new Date(fix.lastUpdated), {
                      addSuffix: true,
                    })
                  : "—"}
              </ClickableTableCell>
              <ClickableTableCell
                sx={{ width: "10%", minWidth: "90px", pr: 0 }}
                href={href}
              >
                <SeverityChip severity={fix.riskValue ?? undefined} small />
              </ClickableTableCell>
              <ClickableTableCell sx={{ width: "50%", pr: 0 }} href={href}>
                {fix.id.startsWith("CVE-") ? (
                  <>
                    <Typography
                      component="span"
                      fontWeight="bold"
                      fontSize="inherit"
                    >
                      {fix.id}
                    </Typography>
                    {fix.title && fix.title !== fix.id && (
                      <Typography component="span" fontSize="inherit">
                        : {fix.title}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    component="span"
                    fontWeight="bold"
                    fontSize="inherit"
                  >
                    {fix.title || fix.id}
                  </Typography>
                )}
              </ClickableTableCell>
              <ClickableTableCell
                sx={{ width: "25%", minWidth: "80px" }}
                href={href}
              >
                {fix.spaceName}
              </ClickableTableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </DashboardTable>
  );
}
